import React from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import {
    Box,
    Container,
    Heading,
    Text,
    Button,
    Link,
    VStack,
    SimpleGrid,
    Image,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";

export default function HRBP() {
    return (
        <Box bg="offwhite.100" minH="100vh">
            <Helmet>
                <title>Psychometric storage, access and talent ID | BetaPersona</title>
                <meta name="description" content="Decide how test results are stored and who can see them. Assessment data management for CIPD-aligned HR business partners." />
            </Helmet>

            <Container maxW="container.xl" py={{ base: 12, md: 20 }}>
                <VStack spacing={12} align="stretch">
                    <Box>
                        <Heading as="h1" size="2xl" mb={6}>
                            Decide how test results are stored, and who can see them.
                        </Heading>
                        <Text fontSize="xl" color="ink.500">
                            CIPD is plain on this: if you use tests, you decide how results are stored and who will have access. After an agency shortlist, the selection scores typically live in GeneSys, a Hogan portal, last year's graduate-campaign folder, and a Word file on a laptop.
                        </Text>
                        <Text fontSize="xl" color="ink.500" mt={4} mb={8}>
                            BetaPersona is the employer's store. Managed access. Selection psychometrics from hire; 360 and performance review later, for internals and development. A pack the department head can read without a psychometric or test-user qualification. The HRBP portal is white-labelled: your brand, not ours.
                        </Text>
                        <Image
                            src="/images/hrbp-8111854.jpeg"
                            alt="Professional office workspace. Photo: Pavel Danilyuk, Pexels"
                            borderRadius="md"
                            w="full"
                            h="auto"
                            objectFit="cover"
                            aspectRatio="16/9"
                        />
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                        <Box bg="navy.500" color="white" p={8} borderRadius="md">
                            <Heading as="h3" size="md" mb={4}>
                                Aligning HR with business goals
                            </Heading>
                            <Text>
                                HRBPs act as consultants to ensure HR processes support the organization's success by aligning talent initiatives with business goals. Those business goals can change radically within a year and come with peaks and troughs. Often speed and agility of HR services falls squarely on the HRBP.
                            </Text>
                        </Box>

                        <Box bg="navy.600" color="white" p={8} borderRadius="md">
                            <Heading as="h3" size="md" mb={4}>
                                Providing strategic input
                            </Heading>
                            <Text>
                                HRBPs provide strategic advice and input on people-related issues such as talent management, employee relations, coaching and development, and organizational development. This input is expected to be backed with empirical evidence as well as offer predictions on future trends.
                            </Text>
                        </Box>

                        <Box bg="navy.700" color="white" p={8} borderRadius="md">
                            <Heading as="h3" size="md" mb={4}>
                                Collaborating with executives
                            </Heading>
                            <Text>
                                HRBPs collaborate with executives and may even sit on the board of directors. This requires constant feedback to executives on operational activities.
                            </Text>
                        </Box>

                        <Box bg="white" p={8} borderRadius="md" shadow="sm" gridColumn={{ base: "span 1", md: "span 3" }}>
                            <Heading as="h3" size="md" mb={4}>
                                Develop and coach department heads
                            </Heading>
                            <Text>
                                Department heads look to their HRBP to advise on the recruitment and development of their teams. This advice has to be given in business-language and the more department heads are empowered to understand the underlying aspects of their workforce the more likely they are to take responsibility for decisions and the less dependent they are on the HRBP for each step in the recruitment and development cycles.
                            </Text>
                        </Box>
                    </SimpleGrid>

                    <Box>
                        <Heading as="h2" size="xl" mb={6}>
                            CIPD: storage and access to assessment data
                        </Heading>
                        <Box bg="white" p={8} borderRadius="md" shadow="sm">
                            <VStack align="start" spacing={4}>
                                <Box>
                                    <Heading as="h4" size="sm" mb={2}>
                                        Psychometrics (selection)
                                    </Heading>
                                    <Text>
                                        Tests you already buy for an agency shortlist or a campaign, used under qualified oversight, stored under your policy. An agency shortlist never includes a 360.
                                    </Text>
                                </Box>
                                <Box>
                                    <Heading as="h4" size="sm" mb={2}>
                                        Performance trends (internal / development)
                                    </Heading>
                                    <Text>
                                        360 and performance assessments belong here, in the same store as the selection battery so development is not a second silo. 360 is only for an internal candidate, or later.
                                    </Text>
                                </Box>
                                <Box>
                                    <Heading as="h4" size="sm" mb={2}>
                                        Storage and access
                                    </Heading>
                                    <Text>
                                        After an agency shortlist: who can see the Hogan (or other selection psychometric) and who can generate the manager pack. Separately, for internals and development: who can see a 360. What happens when the person moves team or country.
                                    </Text>
                                </Box>
                            </VStack>
                        </Box>
                    </Box>

                    <Box>
                        <Heading as="h2" size="xl" mb={6}>
                            What you do currently
                        </Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    Shortlist arrives
                                </Heading>
                                <Text>
                                    Agency sends three candidates. Talent team asks for Hogan HPI + HDS + MVPI, plus a 360 if internal. You already have a GeneSys account and a Hogan portal login. You also have a 360 platform.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    Fire the assessments
                                </Heading>
                                <Text>
                                    Log into GeneSys, create the battery, email the link. Log into Hogan, create the invitation, email the link. Log into 360 platform, create the invite. Wait for publisher email saying candidate completed.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    Collate the reports
                                </Heading>
                                <Text>
                                    Log into GeneSys, download PDF. Log into Hogan, download three PDFs. Download 360 report. Open five PDFs, pull scale scores, paste into Word template mapped to your competency framework.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    Write the pack
                                </Heading>
                                <Text>
                                    Add narrative. Add context. Explain what the scores mean for this role. Write the hiring-manager-friendly version. This is qualified work. The line manager cannot do it — they do not have a psychometric or test-user qualification.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    Store the files
                                </Heading>
                                <Text>
                                    Where do the publisher PDFs live? Laptop folder? SharePoint? Email attachment? What is the access policy? Can the hiring manager see the raw Hogan report? Who decides?
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    Repeat for promotion
                                </Heading>
                                <Text>
                                    Three years later the person is up for promotion. Where is the original GeneSys report? Who has access? Do you re-assess or pull the old file? How do you compare against the competency framework for the new role?
                                </Text>
                            </Box>
                        </SimpleGrid>
                    </Box>

                    <Box>
                        <Heading as="h2" size="xl" mb={6}>
                            What goes wrong today
                        </Heading>
                        <VStack align="start" spacing={6}>
                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    Portal-hopping
                                </Heading>
                                <Text>
                                    Three publisher logins. Three different interfaces. GeneSys link expires. Hogan portal session times out. You spend more time logging in than pulling reports.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    Files scattered
                                </Heading>
                                <Text>
                                    Publisher PDFs on your laptop. The pack you wrote is in email. The hiring manager's copy is on SharePoint. The agency has a copy somewhere. The candidate has a copy. No single source of truth. No managed access.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    Qualified professionals collating
                                </Heading>
                                <Text>
                                    You have BPS or EFPA certification. You should be interpreting and advising. Instead you are copying scale scores from PDF to Word. The collating takes longer than the advising.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    Line managers get raw reports
                                </Heading>
                                <Text>
                                    Under time pressure, the temptation is to forward the Hogan PDF to the hiring manager as-is. The hiring manager does not have a psychometric or test-user qualification. They cannot interpret it. They email you for explanations. You have now created more work, not less.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm" w="full">
                                <Heading as="h4" size="sm" mb={3}>
                                    No access policy
                                </Heading>
                                <Text>
                                    Who can see what? The person assessed has GDPR rights. The agency does not own the file after shortlist. The hiring manager should see the derived pack, not the raw GeneSys scale scores. How do you enforce that when files are on laptops and in email?
                                </Text>
                            </Box>
                        </VStack>
                    </Box>

                    <Box>
                        <Heading as="h2" size="xl" mb={6}>
                            What you get with BetaPersona
                        </Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    One repository
                                </Heading>
                                <Text>
                                    Psychometric, 360 and performance assessments in one store. GeneSys, Hogan, SHL, Thomas, 360, custom performance data. One login. One access policy. One audit log.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    Live integrations
                                </Heading>
                                <Text>
                                    Fire the assessment from BetaPersona. Live integration with your chosen publishers e.g. Psytech. Results land in the repository automatically. No logging into publisher portals, no chasing email links, no pulling PDFs by hand.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    Managed access
                                </Heading>
                                <Text>
                                    You decide who can see what. HRBP sees publisher reports. Hiring manager sees the derived business-ready pack. In-house OP advises on interpretation. Audit log records who viewed what, when.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    Business-ready packs
                                </Heading>
                                <Text>
                                    Generate a pack derived from publisher reports and mapped to your competency framework. The hiring manager can use it without a psychometric or test-user qualification. Nobody copy-pastes scale scores into Word.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    White-labelled
                                </Heading>
                                <Text>
                                    The client portal carries your organization's brand, not BetaPersona. Your people log into a portal that looks like your HRIS, not a third-party assessment platform.
                                </Text>
                            </Box>

                            <Box bg="white" p={6} borderRadius="md" shadow="sm">
                                <Heading as="h4" size="sm" mb={3}>
                                    GDPR-aligned storage
                                </Heading>
                                <Text>
                                    Psychometric data is special-category. Data processing agreement covers storage, access and generation. The employer is data controller. BetaPersona is data processor. The person assessed has GDPR rights.
                                </Text>
                            </Box>
                        </SimpleGrid>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            "We already have GeneSys / Hogan / Thomas"
                        </Heading>
                        <Text mb={4}>
                            Keep them. This is an overlay, not a replacement. We do not host rival tests to Psytech, Hogan, SHL etc. We connect to your existing publisher accounts, hold the file, apply your access policy, and generate the pack.
                        </Text>
                        <Text>
                            The publisher still owns the test library, the normative data, and the scale-level scoring. BetaPersona stores the result, manages access, and produces the business-ready report derived from those publisher scores and your framework.
                        </Text>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            "Agencies should just do this"
                        </Heading>
                        <Text mb={4}>
                            After the shortlist arrives, you own the person—not the agency. The agency's contract ends. Their files, their logins, their 90-day publisher retention window—all gone.
                        </Text>
                        <Text mb={4}>
                            You need the assessment data for promotion, development, succession planning. The HRBP decides access policy. The HRBP decides who sees raw reports and who sees business-ready packs. That requires your system, not the agency's system.
                        </Text>
                        <Text>
                            If the agency uses BetaPersona on your behalf, fine. But the repository, the access policy, and the packs belong to you—the employer—after shortlist.
                        </Text>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            "Will line managers ignore the psychometric pack?"
                        </Heading>
                        <Text mb={4}>
                            If the line manager receives a raw Hogan PDF with scale scores and no context, yes — they will ignore it or misinterpret it. They do not have a psychometric or test-user qualification. They cannot use it. They will ask you to explain it, creating more work.
                        </Text>
                        <Text mb={4}>
                            If the line manager receives a business-ready pack derived from those Hogan scores and mapped to the competencies for this role, with narrative explaining what the scores mean in context—now they can use it. The pack is written in business language, not psychometric jargon.
                        </Text>
                        <Text>
                            The occupational psychologist or BPS-qualified HRBP still advises. We remove the collating. The qualified professional spends time interpreting and advising, not copy-pasting scale scores from PDF to Word.
                        </Text>
                    </Box>

                    <Box bg="white" p={8} borderRadius="md" shadow="sm">
                        <Heading as="h3" size="lg" mb={4}>
                            Special-category data
                        </Heading>
                        <Text mb={4}>
                            Psychometric assessment data is special-category personal data under GDPR Article 9. The HRBP is responsible for ensuring lawful processing, secure storage, and qualified test use.
                        </Text>
                        <Text>
                            BetaPersona provides the repository, the access policy, and the audit log. Data processing agreement covers controller / processor responsibilities. The employer (you) is data controller. BetaPersona is data processor. The person assessed has rights to access, rectification, and erasure.
                        </Text>
                        <Link as={ReactRouterLink} to="/security-and-data" color="navy.500" fontWeight="600">
                            Security & data →
                        </Link>
                    </Box>

                    <Box bg="navy.500" color="white" p={10} borderRadius="md" textAlign="center">
                        <Heading as="h2" size="xl" mb={6}>
                            Talk to us
                        </Heading>
                        <Text fontSize="lg" mb={8}>
                            We will walk you through the repository, integrations, access policy, and pack generation. Bring your in-house OP or talent team.
                        </Text>
                        <Link
                            as={ReactRouterLink}
                            to="/contact"
                            _hover={{ textDecoration: "none" }}
                        >
                            <Button
                                bg="accent.orange"
                                color="white"
                                size="lg"
                                _hover={{ bg: "rgba(248, 100, 30, 0.85)" }}
                            >
                                Talk to us
                            </Button>
                        </Link>
                        <Text mt={4} fontSize="sm">
                            A working session with talent, HRBP or in-house occupational psychology.
                        </Text>
                    </Box>
                </VStack>
            </Container>
        </Box>
    );
}
